import { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { HiExternalLink } from "react-icons/hi";
import { CreditCard } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../store/auth-context";
import PaywallModal from "./PaywallModal";
import { usePremiumAccess } from "../hooks/usePremiumAccess";
import { GetUserById } from "../services/backend";
import {
  canStartStripeCheckout,
  startStripeBillingPortal,
  syncStripeSubscription,
} from "../services/stripeCheckout";
import {
  isManualPremiumValid,
  isStripePremiumActive,
  isUserPremium,
  premiumUntilLabel,
} from "../util/premiumAccess";

export default function SettingsMembership() {
  const { t } = useTranslation();
  const toast = useToast();
  const { authenticatedUser, authenticateUser } = useContext(AuthContext);
  const userId = authenticatedUser?.id;
  const user = authenticatedUser;
  const { isPremium, checking, refreshPremiumStatus } = usePremiumAccess();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [openingPortal, setOpeningPortal] = useState(false);
  const syncedUserIdRef = useRef(null);
  const authenticateUserRef = useRef(authenticateUser);
  const refreshPremiumStatusRef = useRef(refreshPremiumStatus);
  authenticateUserRef.current = authenticateUser;
  refreshPremiumStatusRef.current = refreshPremiumStatus;

  const titleColor = useColorModeValue("gray.900", "white");
  const muted = useColorModeValue("gray.600", "whiteAlpha.600");
  const sectionBorder = useColorModeValue("gray.200", "whiteAlpha.100");
  const panelBg = useColorModeValue("rgba(255,255,255,0.75)", "rgba(255,255,255,0.04)");
  const activeBg = useColorModeValue("brand.50", "rgba(52,211,153,0.1)");
  const inactiveBg = useColorModeValue("gray.50", "rgba(255,255,255,0.03)");

  const isManual = isManualPremiumValid(user);
  const isStripe = isStripePremiumActive(user);
  const premiumActive = isPremium || isUserPremium(user) || isManual || isStripe;
  const premiumUntil = premiumUntilLabel(user);
  const stripeConfigured = canStartStripeCheckout();

  // One primary CTA only:
  // - Active Stripe sub → Manage (portal)
  // - Not premium → Upgrade
  // - Admin grant → neither (status only)
  const showManage = Boolean(stripeConfigured && userId && isStripe && !isManual);
  const showUpgrade = Boolean(!premiumActive && !isManual && !isStripe && !checking);

  const statusKey = checking
    ? "checking"
    : isManual
      ? "manual"
      : isStripe
        ? "stripe"
        : "free";

  // Sync once per user visit — avoid AuthCtx/refresh deps that re-trigger forever.
  useEffect(() => {
    if (!userId) return undefined;
    if (syncedUserIdRef.current === userId) return undefined;
    syncedUserIdRef.current = userId;

    let cancelled = false;
    (async () => {
      try {
        await syncStripeSubscription(userId);
        if (cancelled) return;
        await refreshPremiumStatusRef.current();
        if (cancelled) return;
        const fresh = await GetUserById(userId);
        if (!cancelled && fresh) authenticateUserRef.current(fresh);
      } catch {
        /* ignore — user may not have Stripe billing yet */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  async function handleManageMembership() {
    if (!userId || !showManage) return;
    if (!stripeConfigured) {
      toast({
        title: t("settings.membershipPortalFailed"),
        description: t("settings.membershipPortalFailedHint"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setOpeningPortal(true);
    try {
      try {
        await syncStripeSubscription(userId);
        const fresh = await GetUserById(userId);
        if (fresh) authenticateUserRef.current(fresh);
      } catch {
        /* portal may still resolve customer by email */
      }

      await startStripeBillingPortal({
        userId,
        returnUrl: `${window.location.origin}/settings`,
      });
    } catch (error) {
      console.error("Billing portal failed:", error);
      toast({
        title: t("settings.membershipPortalFailed"),
        description: error?.message || t("settings.membershipPortalFailedHint"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setOpeningPortal(false);
    }
  }

  const statusLabel = {
    checking: t("settings.membershipStatusChecking"),
    manual: t("settings.membershipStatusActive"),
    stripe: t("settings.membershipStatusActive"),
    free: t("settings.membershipStatusFree"),
  }[statusKey];

  const statusDetail = {
    checking: t("settings.membershipCheckingHint"),
    manual: premiumUntil
      ? t("settings.membershipManualUntil", { date: premiumUntil })
      : t("settings.membershipManualNoExpiry"),
    stripe: t("settings.membershipStripeHint"),
    free: t("settings.membershipFreeHint"),
  }[statusKey];

  const actionsIntro = showManage
    ? t("settings.membershipManageIntro")
    : isManual
      ? t("settings.membershipManualIntro")
      : t("settings.membershipUpgradeIntro");

  return (
    <>
      <Box
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={sectionBorder}
        bg={panelBg}
        backdropFilter="blur(8px)"
        overflow="hidden"
      >
        <Flex
          px={{ base: 4, md: 6 }}
          py={4}
          borderBottomWidth="1px"
          borderColor={sectionBorder}
          align="center"
          gap={2}
        >
          <Box display={{ base: "none", lg: "inline-flex" }} color="brand.500">
            <CreditCard size={18} strokeWidth={2} />
          </Box>
          <Text fontSize="sm" fontWeight="semibold" color={titleColor}>
            {t("settings.membershipTitle")}
          </Text>
        </Flex>

        <Box p={{ base: 4, md: 6 }}>
          <Box
            p={4}
            borderRadius="xl"
            borderWidth="1px"
            borderColor={sectionBorder}
            bg={premiumActive ? activeBg : inactiveBg}
          >
            <Text fontSize="xs" fontWeight="semibold" letterSpacing="0.08em" textTransform="uppercase" color="brand.500" mb={1}>
              {t("settings.membershipPlanLabel")}
            </Text>
            <Text fontSize="lg" fontWeight="bold" color={titleColor} lineHeight="1.2">
              {statusLabel}
            </Text>
            <Text fontSize="sm" color={muted} mt={2} lineHeight="tall">
              {statusDetail}
            </Text>
          </Box>

          <Box mt={4}>
            <Text fontSize="xs" fontWeight="semibold" letterSpacing="0.08em" textTransform="uppercase" color={muted} mb={2}>
              {t("settings.membershipActionsLabel")}
            </Text>
            <Text fontSize="sm" color={muted} mb={4} lineHeight="tall">
              {actionsIntro}
            </Text>

            {showManage ? (
              <Button
                w="100%"
                size="md"
                fontWeight="semibold"
                borderRadius="full"
                bgGradient="linear(to-r, brand.500, brand.600)"
                color="white"
                _hover={{ opacity: 0.92 }}
                isLoading={openingPortal}
                loadingText={t("settings.membershipOpeningPortal")}
                rightIcon={<Icon as={HiExternalLink} />}
                onClick={handleManageMembership}
              >
                {t("settings.membershipManageCta")}
              </Button>
            ) : null}

            {showUpgrade ? (
              <Button
                w="100%"
                size="md"
                fontWeight="semibold"
                borderRadius="full"
                bgGradient="linear(to-r, brand.500, brand.600)"
                color="white"
                _hover={{ opacity: 0.92 }}
                onClick={onOpen}
              >
                {t("settings.premiumUpsellCta")}
              </Button>
            ) : null}
          </Box>

          {showManage ? (
            <Box mt={4} p={3} borderRadius="lg" borderWidth="1px" borderColor={sectionBorder}>
              <Text fontSize="xs" color={muted} lineHeight="tall">
                {t("settings.membershipCancelHint")}
              </Text>
            </Box>
          ) : null}
        </Box>
      </Box>

      <PaywallModal isOpen={isOpen} onClose={onClose} userId={userId} size="2xl" />
    </>
  );
}
